import { useState, useContext } from "react";
import api_endpoints from "../../api";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import AuthContext from "../../context/AuthContext/AuthContext";
import {
  dateTimeReadableSimple,
  numberWithCommas,
  validEmail,
  formatDateTime,
} from "../../utils";
import dayjs from "dayjs";
import {
  MenuItem,
  Select,
  Chip,
  Button,
  TextField,
  Divider,
  Switch,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from "@mui/material";
import PredefinedPeriods from "../../widgets/CustomDatePicker/PredefinedPeriod";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import OutboxIcon from "@mui/icons-material/Outbox";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useApiClientQuery,
  useApiClientMutation,
} from "../../hooks/useApiClient";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";

import ExportChooser from "./ExportChooser";
import EmailExportDialog from "./EmailExportDialog";

const ManageTransactions = () => {
  const authContext = useContext(AuthContext);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const [campaignsMenuOption, setCampaignsMenuOption] = useState([]);
  const [rows, setRows] = useState([]);
  const [kiosksMenuOption, setKiosksMenuOption] = useState([]);
  const [emplacementData, setEmplacementData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [emailError, setEmailError] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const { t } = useCustomTranslation("Transactions");
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  const [tableFilter, setTableFilter] = useState({
    organization_id: authContext.authContext.accessToken.organization_id,
    amount: "",
    location_name: "",
    donor_email: "",
    campaignName: "",
    kioskName: "",
    emplacement: "",
    donor: "",
    campagne: "",
    kiosk: "",
    date: dayjs(new Date()),
    start_date: dayjs(new Date()),
    end_date: dayjs(new Date()),
    start_time: dayjs().startOf("day").format("HH:mm"),
    end_time: dayjs().endOf("day").format("HH:mm"),
  });
  const [formData, setFormData] = useState({
    exportFormat: "csv",
    recurring: false,
    recurringDay: 1,
    timeOfDay: dayjs().startOf("day"),
    endDate: null,
    emails: [],
  });

  const handlePeriodChange = (event) => setSelectedPeriod(event.target.value);
  const changeFormData = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));
  const setpersonalizedDates = (startDate, endDate, starttime, endtime) => {
    setTableFilter({
      ...tableFilter,
      start_date: startDate,
      end_date: endDate,
      start_time: starttime.format("HH:mm"),
      end_time: endtime.format("HH:mm"),
    });
  };

  const columns = [
    { field: "amount", headerName: t("transactions_table_amount"), sortable: true, numberSort: true },
    { field: "location_name", headerName: t("transactions_table_location"), sortable: true, numberSort: false },
    { field: "donor_email", headerName: t("transactions_table_donorEmail"), sortable: true, numberSort: false },
    { field: "campaignName", headerName: t("transactions_table_campaign"), sortable: true, numberSort: false },
    { field: "kioskName", headerName: t("transactions_table_kiosk"), sortable: true, numberSort: false },
    { field: "date", headerName: t("transactions_table_period"), sortable: true, numberSort: false },
  ];

  const filters = {
    amount: (
      <CustomTextField
        value={tableFilter.amount || ""}
        onChange={(e) => changeTableFilter("amount", e.target.value)}
        clearFilter={() => clearTableFilter("amount")}
      />
    ),
    location_name: (
      <Select
        size="small"
        variant="outlined"
        className="w-60"
        sx={{ width: "100%" }}
        fullWidth
        displayEmpty
        value={tableFilter.location_name}
        onChange={(e) => {
          changeTableFilter("location_name", e.target.value);
        }}
      >
        <MenuItem value={""} style={{ display: emplacementData.length <= 1 ? "none" : "block" }}>
          {t("transactions_table_dropdown_all_option")}
        </MenuItem>
        {emplacementData.map((emplacement, index) => (
          <MenuItem key={emplacement._id} value={emplacement._id} selected={emplacementData.length === 1 && index === 0}>
            <div className="flex items-center justify-between">
              <span className="mr-2.5">{emplacement.location_name}</span>
            </div>
          </MenuItem>
        ))}
      </Select>
    ),
    donor_email: (
      <CustomTextField
        value={tableFilter.donor_email || ""}
        onChange={(e) => changeTableFilter("donor_email", e.target.value)}
        clearFilter={() => clearTableFilter("donor_email")}
      />
    ),
    campaignName: (
      <CustomSelect
        value={tableFilter.campaignName}
        onChange={(e) => {
          changeTableFilter("campaignName", e.target.value);
        }}
        options={campaignsMenuOption}
      />
    ),
    kioskName: (
      <CustomSelect
        value={tableFilter.kioskName}
        onChange={(e) => {
          changeTableFilter("kioskName", e.target.value);
        }}
        options={kiosksMenuOption}
      />
    ),
    date: (
      <PredefinedPeriods
        selectedPeriod={selectedPeriod}
        handlePeriodChange={handlePeriodChange}
        setSelectedPeriod={setSelectedPeriod}
        setpersonalizedDates={setpersonalizedDates}
      />
    ),
  };

  const kiosksFilterQuery = useApiClientQuery(
    () => api_endpoints.getKiosks(authContext.authContext.accessToken.organization_id),
    { queryKey: [`kiosksFilter_organizationid_${tableFilter.organization_id}`] },
    (data) => {
      if (data.length == 0) {
        setKiosksMenuOption([{ value: "", label: t("transactions_table_dropdown_all_option") }]);
        return;
      }
      const options = data.map((kiosk) => ({
        value: kiosk._id,
        label: (
          <div className="flex flex-1 items-center justify-between">
            <span>{kiosk.name}</span>
            <div>
              <Chip
                color={kiosk.is_active ? "success" : "error"}
                label={kiosk.is_active ? t("transactions_table_active") : t("transactions_table_inactive")}
                size="small"
              />
            </div>
          </div>
        ),
      }));
      options.unshift({ value: "", label: t("transactions_table_dropdown_all_option") });
      setKiosksMenuOption(options);
    }
  );

  const locationsFilterQuery = useApiClientQuery(
    () => api_endpoints.getLocations(authContext.authContext.accessToken.organization_id),
    { queryKey: [`locationFilter_organizationid_${tableFilter.organization_id}`] },
    (data) => {
      if (data.length > 0) {
        setEmplacementData(data);
        if (data.length === 1) changeTableFilter("emplacement", data[0]._id);
      }
    }
  );

  const campaignFilterQuery = useApiClientQuery(
    () => api_endpoints.getCampaigns(authContext.authContext.accessToken.organization_id),
    { queryKey: [`campaignFilter_organizationid_${tableFilter.organization_id}`] },
    (data) => {
      const options = data.map((campaign) => ({
        value: campaign._id,
        label: (
          <div className="flex flex-1 items-center justify-between">
            <span>{campaign.name}</span>
            <div>
              <Chip
                color={campaign.enabled ? "success" : "error"}
                label={campaign.enabled ? t("transactions_table_active") : t("transactions_table_inactive")}
                size="small"
              />
            </div>
          </div>
        ),
      }));
      options.unshift({ value: "", label: t("transactions_table_dropdown_all_option") });
      setCampaignsMenuOption(options);
    }
  );

  const transactionsQuery = useApiClientQuery(
    () =>
      api_endpoints.getTransactions(
        authContext.authContext.accessToken.organization_id,
        tableFilter.amount,
        tableFilter.location_name,
        tableFilter.donor_email,
        tableFilter.campaignName,
        tableFilter.kioskName,
        formatDateTime(tableFilter.start_date, tableFilter.start_time),
        formatDateTime(tableFilter.end_date, tableFilter.end_time)
      ),
    { queryKey: ["transactions", tableFilter, authContext.authContext.accessToken.organization_id] },
    (data) => {
      const formattedData = data.map((transaction) => ({
        id: transaction._id,
        amount: `${numberWithCommas(transaction.amount)}$`,
        exportAmount: transaction.amount,
        campaignName: transaction.campaign?.name,
        location_name: transaction.location?.location_name,
        location_add: transaction.campaign_location?.address,
        donor_email: transaction.donor?.email,
        kioskName: transaction.kiosk?.name,
        date: dateTimeReadableSimple(transaction.created_at),
      }));
      setRows(formattedData);
    }
  );

  const isLoading =
    kiosksFilterQuery.isLoading ||
    locationsFilterQuery.isLoading ||
    campaignFilterQuery.isLoading ||
    transactionsQuery.isLoading;

  const changeTableFilter = (option, value) =>
    setTableFilter((prev) => ({ ...prev, [option]: value }));

  const clearTableFilter = (option) => {
    setTableFilter((prev) => ({ ...prev, [option]: "" }));
    if (emplacementData.length === 1) changeTableFilter("location_name", emplacementData[0]._id);
  };

  // download helpers kept local
  const triggerBrowserDownload = (blob, fileNameWithExt) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileNameWithExt;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return "";
    const s = String(value);
    return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const formatCsvDecimal = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return String(value).replace(".", ",");
  };

  const toCsv = (exportRows, headers) => {
    const lines = [
      headers.map((h) => escapeCsvCell(h.label)).join(";"),
      ...exportRows.map((r) =>
        headers
          .map((h) =>
            escapeCsvCell(
              h.key === "amount"
                ? formatCsvDecimal(r.exportAmount ?? r[h.key])
                : r[h.key]
            )
          )
          .join(";")
      ),
    ];
    return lines.join("\n");
  };

  const toXlsxBlob = async (exportRows, headers) => {
    try {
      const XLSX = await import("xlsx");
      const ordered = exportRows.map((r) =>
        headers.reduce((acc, h) => {
          acc[h.label] = r[h.key];
          return acc;
        }, {})
      );
      const ws = XLSX.utils.json_to_sheet(ordered);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      return new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    } catch {
      return null;
    }
  };

  const handleDownload = async (format) => {
    if (!rows.length) return;

    const headers = [
      { key: "amount", label: t("transactions_table_amount") },
      { key: "location_name", label: t("transactions_table_location") },
      { key: "donor_email", label: t("transactions_table_donorEmail") },
      { key: "campaignName", label: t("transactions_table_campaign") },
      { key: "kioskName", label: t("transactions_table_kiosk") },
      { key: "date", label: t("transactions_table_period") },
    ];

    const fileBase = `transactions_${dayjs().format("YYYYMMDD_HHmmss")}`;

    if (format === "xlsx") {
      const blob = await toXlsxBlob(rows, headers);
      if (blob) {
        triggerBrowserDownload(blob, `${fileBase}.xlsx`);
        setChooserOpen(false);
        return;
      }
      console.warn("xlsx not available; falling back to CSV.");
    }

    const csv = toCsv(rows, headers);
    triggerBrowserDownload(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${fileBase}.csv`);
    setChooserOpen(false);
  };

  // email flow
  const { mutate: emailOnce, reset: resetEmailOnce, isPending: isSendPending, isError: isSendError } =
    useApiClientMutation(api_endpoints.emailTransactionExport, [
      `export_NonRecurring_Transactions_${tableFilter.organization_id}`,
    ]);
  const { mutate: emailRecurring, reset: resetEmailRecurring, isPending: isRecPending, isError: isRecError } =
    useApiClientMutation(api_endpoints.createRecurringTransactionExport, [
      `export_recurring_transactions_${tableFilter.organization_id}`,
    ]);

  const handleSuccessfulMutation = (msg) => {
    setMessageSuccessAlert(msg);
    setShowSuccessAlert(true);
    closeEmailModal();
  };

  const handleEmailSubmit = () => {
    const commonData = {
      format: formData.exportFormat,
      data: rows,
      emails: Array.isArray(formData.emails) ? formData.emails : [formData.emails],
    };

    const recurringData = {
      ...commonData,
      dayOfMonth: formData.recurringDay,
      timeOfDay: (() => {
        const time = formData.timeOfDay.$d;
        return `${time.getHours().toString().padStart(2, "0")}:${time
          .getMinutes()
          .toString()
          .padStart(2, "0")}`;
      })(),
      endDate: formData.endDate,
      filters: tableFilter,
      userId: authContext.authContext.accessToken.id,
      range: selectedPeriod,
    };

    if (formData.recurring) {
      emailRecurring(recurringData, { onSuccess: handleSuccessfulMutation });
    } else {
      emailOnce(commonData, { onSuccess: handleSuccessfulMutation });
    }
  };

  const openChooser = () => setChooserOpen(true);
  const openEmailModal = () => setEmailModalOpen(true);

  const closeEmailModal = () => {
    setEmailModalOpen(false);
    setFormData({
      exportFormat: "csv",
      recurring: false,
      recurringDay: 1,
      timeOfDay: dayjs().startOf("day"),
      endDate: null,
      emails: [],
    });
    setEmailInput("");
    setEmailError("");
    resetEmailOnce();
    resetEmailRecurring();
  };

  const handleAddEmail = () => {
    const ok = validEmail(emailInput);
    if (!ok) {
      setEmailError("Invalid email format");
      return;
    }
    if (emailInput && !formData.emails.includes(emailInput)) {
      changeFormData("emails", [...formData.emails, emailInput]);
      setEmailInput("");
      setEmailError("");
    } else if (formData.emails.includes(emailInput)) {
      setEmailError("Email already added");
    }
  };

  const handleDeleteEmail = (email) =>
    changeFormData(
      "emails",
      formData.emails.filter((e) => e !== email)
    );

  const handleRecurringDayChange = (value) => {
    if (value === "" || (+value >= 1 && +value <= 31)) {
      changeFormData("recurringDay", value);
    }
  };

  return (
    <>
      <ExportChooser
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onDownload={(fmt) => handleDownload(fmt)}
        onEmail={() => {
          setChooserOpen(false);
          openEmailModal();
        }}
        initialFormat={formData.exportFormat}
        disableDownload={rows.length === 0}
      />

      <EmailExportDialog
        open={emailModalOpen}
        emailInput={emailInput}
        emails={formData.emails}
        emailError={emailError}
        exportFormat={formData.exportFormat}
        recurring={!!formData.recurring}
        recurringDay={formData.recurringDay}
        isSubmitting={isSendPending || isRecPending}
        hasError={isSendError || isRecError}
        onClose={closeEmailModal}
        onChangeEmailInput={setEmailInput}
        onAddEmail={handleAddEmail}
        onDeleteEmail={handleDeleteEmail}
        onChangeExportFormat={(fmt) => changeFormData("exportFormat", fmt)}
        onToggleRecurring={(checked) => changeFormData("recurring", checked)}
        onChangeRecurringDay={handleRecurringDayChange}
        onSubmit={handleEmailSubmit}
        timePicker={
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr-ca">
            <TimePicker
              label={t("transactions_table_export_time_of_day")}
              value={formData.timeOfDay}
              onChange={(newTime) => changeFormData("timeOfDay", newTime)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
              ampm
            />
          </LocalizationProvider>
        }
        datePicker={
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr-ca">
            <DatePicker
              label={t("transactions_table_export_end_date")}
              value={formData.endDate}
              onChange={(newDate) => changeFormData("endDate", newDate)}
              minDate={dayjs(new Date())}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        }
      />

      <div className="flex justify-end">
        <Tooltip title={rows.length === 0 ? t("transactions_table_export_button_tooltip") : ""} arrow>
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={<OutboxIcon />}
              onClick={openChooser}
              disabled={rows.length === 0}
            >
              {t("transactions_table_export_button")}
            </Button>
          </span>
        </Tooltip>
      </div>

      <div className="flex flex-1 h-full mt-5 flex-col">
        <DataGrid columns={columns} rows={rows} filters={filters} loading={isLoading} />
      </div>
    </>
  );
};

export default ManageTransactions;
